import React, { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useCompanyContext } from "../hooks/useCompanyContext";

const getEvaluationStatusLabel = (status, t) => {
  switch (status) {
    case "pending":
      return t?.evaluationStatusPending || "Nog niet gestart";
    case "in_progress":
      return t?.evaluationStatusInProgress || "Bezig";
    case "completed":
      return t?.evaluationStatusCompleted || "Afgerond";
    case "cancelled":
      return t?.evaluationStatusCancelled || "Geannuleerd";
    default:
      return t?.evaluationStatusUnknown || "Onbekend";
  }
};

const getEvaluationActionLabel = (status, t) => {
  switch (status) {
    case "pending":
      return t?.evaluationActionStart || "Start evaluatie";
    case "in_progress":
      return t?.evaluationActionContinue || "Verder invullen";
    case "completed":
      return t?.evaluationActionCompleted || "Afgerond";
    case "cancelled":
      return t?.evaluationActionCancelled || "Geannuleerd";
    default:
      return t?.evaluationActionStart || "Start evaluatie";
  }
};

const Teelt = () => {
  const { company, t } = useCompanyContext();

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([
    { id: "IA_PromisedDeliveryDate", desc: true },
  ]);

  const filteredData = useMemo(() => {
    const orders = company?.orders || [];

    let result = orders.filter((order) => order.evaluation?.allowed === true);

    if (search) {
      const searchValue = search.toLowerCase();

      result = result.filter((order) => {
        const evaluationStatusLabel = getEvaluationStatusLabel(
          order.evaluation?.status,
          t
        );

        const searchableValues = [
          ...Object.values(order),
          evaluationStatusLabel,
          order.evaluation?.status,
          order.evaluation?.progress,
          order.evaluation?.evaluator,
        ];

        return searchableValues.some((value) =>
          value?.toString().toLowerCase().includes(searchValue)
        );
      });
    }

    result = result.sort((a, b) => {
      const dateA = new Date(a.IA_PromisedDeliveryDate || 0);
      const dateB = new Date(b.IA_PromisedDeliveryDate || 0);
      return dateB - dateA;
    });

    return result.slice(0, 20);
  }, [company?.orders, search, t]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "No",
        header: t?.ordernummer || "Ordernummer",
        meta: { className: "col-ordernummer" },
      },
      {
        id: "evaluationStatus",
        header: t?.evaluationStatusHeader || "Evaluatie status",
        cell: ({ row }) => {
          const status = row.original.evaluation?.status;
          const progress = row.original.evaluation?.progress ?? 0;

          return (
            <span>
              {getEvaluationStatusLabel(status, t)}
              {status === "in_progress" ? ` (${progress}%)` : ""}
            </span>
          );
        },
        meta: { className: "col-ordernummer" },
      },
      {
        id: "evaluatie",
        header: "",
        meta: { className: "col-detail" },
        cell: ({ row }) => {
          const status = row.original.evaluation?.status;
          const actionLabel = getEvaluationActionLabel(status, t);

          if (status === "completed") {
            return (
              <span className="status-completed">
                {t?.evaluationStatusCompleted || "Afgerond"}
              </span>
            );
          }

          if (status === "cancelled") {
            return (
              <span className="status-cancelled">
                {t?.evaluationStatusCancelled || "Geannuleerd"}
              </span>
            );
          }

          return (
            <a
              href={`/evaluatie?order=${row.original.No}`}
              className="download-button"
            >
              {actionLabel}
            </a>
          );
        },
      },
      {
        accessorKey: "Ship_to_Name",
        header: t?.verzendnaam || "Verzendnaam",
        meta: { className: "col-verzendnaam" },
      },
      {
        accessorKey: "IA_PromisedDeliveryDate",
        header: t?.leverdatum || "Leverdatum",
        meta: { className: "col-leverdatum" },
        cell: (info) => {
          const dateValue = info.getValue();

          return dateValue
            ? new Date(dateValue).toLocaleDateString("nl-NL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : t?.notAvailable || "N.v.t.";
        },
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  if (!company || !company.orders || company.orders.length === 0) {
    return (
      <div>
        <h1 className="pageheading">
          {t?.evaluationsPageTitle || "Evaluaties"}
        </h1>
        <p>{t?.noOrdersAvailable || "Geen orders beschikbaar."}</p>
      </div>
    );
  }

  return (
    <div className="order-table">
      <h1 className="pageheading">
        {t?.evaluationsPageTitle || "Evaluaties"}
      </h1>

      <div className="search-container">
        <input
          type="text"
          placeholder={
            t?.evaluationSearchPlaceholder ||
            "Zoek op ordernummer, klant, status of datum..."
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredData.length === 0 ? (
        <p>
          {t?.evaluationNoReleasedOrders ||
            "Geen orders beschikbaar waarvoor een evaluatie is vrijgegeven."}
        </p>
      ) : (
        <>
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {t?.previous || "Vorige"}
            </button>

            <span>
              {t?.page || "Pagina"}{" "}
              <strong>
                {table.getState().pagination.pageIndex + 1}{" "}
                {t?.of || "van"} {table.getPageCount()}
              </strong>
            </span>

            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {t?.next || "Volgende"}
            </button>
          </div>
        </>
      )}

      <style>
        {`
          .status-completed {
            display: inline-block;
            padding: 8px 10px;
            color: #2e7d32;
            font-weight: 600;
          }

          .status-cancelled {
            display: inline-block;
            padding: 8px 10px;
            color: #999;
            font-weight: 600;
          }

          @media (max-width: 1100px) {
            .col-verzendnaam {
              display: none;
            }
          }

          @media (max-width: 900px) {
            .col-naam {
              display: none;
            }
          }

          @media (max-width: 700px) {
            table th,
            table td {
              font-size: 13px;
              padding: 6px 8px;
            }

            .download-button {
              padding: 6px 8px;
              font-size: 13px;
            }
          }

          .order-table {
            overflow-x: auto;
          }
        `}
      </style>
    </div>
  );
};

export default Teelt;